import React, {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from "react";
import {
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CBadge,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilHistory, cilDescription, cilTrash } from "@coreui/icons";

import {
  useLocalDrafts,
  applyDraftFields,
  deepEqualByContent,
} from "../_hooks/useLocalDrafts";
import DiffModal from "./DiffModal"; // usa o modal em lista com HTML sanitizado

const DraftRecovery = forwardRef(function DraftRecovery(
  {
    /** Identificação */
    appId,
    notice,
    schemaVersion = "v1",

    /** Estado da candidatura */
    baseGetter, // () => application.content
    currentGetter, // () => formData
    setFormData,

    /** Feedback */
    setToast,

    /** Controlos visuais (opcional): força esconder/mostrar na toolbar */
    forceHide = false,
  },
  ref
) {
  const {
    hasPrev,
    getPrev,
    clearPrev,
    clearCurr,
    clearAll,
    scheduleCurrSave,
    saveCurrNow,
    mergePrevIntoCurr,
  } = useLocalDrafts({
    appId,
    notice,
    schemaVersion,
    baseGetter,
    currentGetter,
    ttlDays: 7,
  });

  // MOSTRAR NA TOOLBAR: só se existia prev ao abrir (verificado 1x)
  const [visible, setVisible] = useState(false);
  const decided = useRef(false);

  useEffect(() => {
    if (decided.current) return;
    decided.current = true;
    setVisible(hasPrev());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Expor API para o pai (limpar todos drafts quando o servidor guardar)
  useImperativeHandle(ref, () => ({
    clearAllDrafts: () => clearAll(),
    scheduleCurrSave,
    saveCurrNow,
  }));

  /** Ações toolbar */
  const [showModal, setShowModal] = useState(false);
  const [changedCache, setChangedCache] = useState({});

  const openModal = () => {
    const changed = getPrev() || {};
    // evitar falsos positivos (no caso de ordem de keys) filtrando por conteúdo
    const base = baseGetter?.() || {};
    const filtered = {};
    for (const k of Object.keys(changed)) {
      if (!deepEqualByContent(changed[k], base[k])) filtered[k] = changed[k];
    }
    setChangedCache(filtered);
    setShowModal(true);
  };

  const handleRestore = () => {
    const changed = getPrev();
    if (!changed || Object.keys(changed).length === 0) return;
    setFormData((prev) => applyDraftFields(prev, changed)); // aplica campos inteiros
    mergePrevIntoCurr(); // funde prev -> curr
    setVisible(false); // não volta a aparecer nesta sessão
    clearPrev(); // rascunho anterior já consumido
    setToast?.({
      title: "Rascunho",
      message: "Rascunho restaurado.",
      type: "success",
    });
  };

  const handleIgnore = () => {
    clearPrev();
    setVisible(false);
    setToast?.({
      title: "Rascunho",
      message: "Rascunho descartado.",
      type: "success",
    });
  };

  const count = useMemo(() => {
    return Object.keys(getPrev() || {}).length;
  }, [visible, getPrev]);

  if (forceHide || !visible) return null;

  return (
    <>
      <CDropdown alignment="end">
        <CDropdownToggle
          color="primary"
          variant="outline"
          className="d-flex align-items-center"
        >
          <CIcon icon={cilHistory} className="me-2" />
          Rascunho
          {count > 0 && (
            <CBadge color="primary" size="sm" className="ms-2">
              {count}
            </CBadge>
          )}
        </CDropdownToggle>
        <CDropdownMenu>
          <CDropdownItem
            className="d-flex align-items-center"
            onClick={openModal}
          >
            <CIcon icon={cilDescription} className="me-2" size="lg" />
            Ver diferenças
          </CDropdownItem>
          <CDropdownItem
            className="d-flex align-items-center"
            onClick={handleRestore}
          >
            <CIcon icon={cilHistory} className="me-2" size="lg" />
            Restaurar rascunho
          </CDropdownItem>
          <CDropdownItem
            className="d-flex align-items-center"
            onClick={handleIgnore}
          >
            <CIcon icon={cilTrash} className="me-2" size="lg" />
            Descartar rascunho
          </CDropdownItem>
        </CDropdownMenu>
      </CDropdown>

      <DiffModal
        visible={showModal}
        onClose={() => setShowModal(false)}
        base={baseGetter?.() || {}}
        changed={changedCache}
      />
    </>
  );
});

export default DraftRecovery;
